import { z } from "zod";

export const createUserFormSchema = z
  .object({
    userId: z.string().min(1, "User Id is required"),
    username: z.string().min(3, "Username must be at least 3 characters"),
    fullName: z.string().min(1, "Full name is required"),
    contactEmail: z.string().email().optional(),
    isAdmin: z.boolean(),
    state: z.enum(["Active", "Inactive"]).optional(),
    sendReport: z.boolean(),
    sendReportEmpty: z.boolean(),
    accessLevel: z.enum(["Customer", "Job", "Global"]),
    customerId: z.string().nullable(),
    jobIds: z
      .array(
        z.object({
          jobName: z.string().nullable(),
          customerId: z.string().nullable(),
          jobId: z.string().nullable(),
        }),
      )
      .nullable()
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.accessLevel === "Customer" && !data.customerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Customer is required",
        path: ["customerId"],
      });
    }

    if (data.accessLevel === "Job" && !data.jobIds) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Jobs are required",
        path: ["accessLevel"],
      });
    }

    // Every job row needs a customer and a job, for any access level
    data.jobIds?.forEach((job, index) => {
      if (!job.customerId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Customer is required",
          path: ["jobIds", index, "customerId"],
        });
      }

      if (!job.jobId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Job is required",
          path: ["jobIds", index, "jobId"],
        });
      }
    });

    if (data.sendReportEmpty && !data.sendReport) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Send report must be enabled to notify when there are no errors",
        path: ["sendReportEmpty"],
      });
    }
  });

export type CreateUserForm = z.infer<typeof createUserFormSchema>;

export const editUserProfileFormSchema = z.object({
  userId: z.string().min(1, "User Id is required"),
  username: z.string().min(3, "Username must be at least 3 characters"),
  fullName: z.string().min(1, "Full name is required"),
});

export type EditUserProfileForm = z.infer<typeof editUserProfileFormSchema>;

export const updatePasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z
      .string()
      .min(8, "Password must be at least 8 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export type UpdatePasswordForm = z.infer<typeof updatePasswordSchema>;

export const passwordChangeFormSchema = z
  .object({
    oldPassword: z.string().min(8, "Password must be at least 8 characters"),
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmNewPassword: z
      .string()
      .min(8, "Password must be at least 8 characters"),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords do not match",
    path: ["confirmNewPassword"],
  });

export type PasswordChangeWithOldForm = z.infer<
  typeof passwordChangeFormSchema
>;

export const utcDateString = z.string().refine(
  (val) => {
    const date = new Date(val);
    return !isNaN(date.getTime()) && val === date.toISOString();
  },
  {
    message: "Invalid UTC date string",
  },
);

export const viewTypeSchema = z.enum(["tickets", "list", "table"]);

export const validColumnOptionSchema = z.enum(["2", "4"]);

export type ValidColumnOption = z.infer<typeof validColumnOptionSchema>;

export const userPreferencesSchema = z.object({
  preferredView: z
    .object({
      desktop: viewTypeSchema,
      mobile: viewTypeSchema,
    })
    .default({
      desktop: "table",
      mobile: "tickets",
    }),
  columns: validColumnOptionSchema.default("2"),
  autoRefresh: z.boolean().default(false),
  tableOrderedFields: z.array(z.string()).optional(),
  tableHiddenColumns: z.record(z.boolean()).optional(),
  tableColumnWidths: z.record(z.number()).optional(),
  tablePinnedColumns: z
    .object({
      left: z.array(z.string()).optional(),
      right: z.array(z.string()).optional(),
    })
    .optional(),
  defaultTableState: z.boolean().default(false).optional(),
});

export type UserPreferences = z.infer<typeof userPreferencesSchema>;
